import { useCallback } from "react";
import { MenuItem } from "@progress/kendo-react-layout";
import { GridCellProps } from "@progress/kendo-react-grid";
import { useSelector } from "react-redux";
import { getUserInfo, isSysAdmin, isTenantAdmin } from "../../app";
import { routes } from "../../app/routes";
import { usePageActions } from "../";
import { LocationModel } from "../../data/entities";
import { PermissionDefinitions } from "../../security";
import { LocationService, UserService } from "../../services";
import { AddLocationModal } from "./components";
import { ODataGrid,
  IconAndText,
  IconDefinitions,
  TitlelessCard,
  ConfirmationModal,
  ResponsiveGridColumn,
  Icon,
  Button, 
  Breakpoints} from "../../components";
import { useCRUDHandlers,
  useGridActions,
  usePermission,
  useQueryParams,
  useSetPageTitle,
  useSharedState } from "../../hooks";
import { createODataQueryFilter } from "../../utils";

export function LocationsIndexPage() {

  const { getQueryParam } = useQueryParams();
  const organizationFilterId = getQueryParam("organizationFilterId");
  const gridData = useSharedState<Array<LocationModel>>([]);
  const locationService = new LocationService();
  const userService = new UserService();
  const { onClick, onSelect, showAddState, showDeleteState, deleteItemState, showDelete } = useGridActions<LocationModel>(routes.location.details);
  const { gridRef, getItems, addItem, deleteItem } = useCRUDHandlers<LocationModel>(locationService, "Location", showAddState);

  const canAddLocation = usePermission(PermissionDefinitions.location.create());
  const canDeleteLocation = usePermission(PermissionDefinitions.location.delete());

  const tenantAdmin = useSelector(isTenantAdmin());
  const systemAdmin = useSelector(isSysAdmin());

  const userInfo = useSelector(getUserInfo());  
  const userLocation = userInfo?.locationIds;

  useSetPageTitle("Locations", []);
  usePageActions(
    canAddLocation && (
      <Button
        onClick={() => {
          showAddState.setter(true);
        }}
      >
        <IconAndText iconName={IconDefinitions.add} text="Add" />
      </Button>
    )
    , [canAddLocation]);

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <>
      {canDeleteLocation && (
        <td className="text-center">
          <Icon
            iconName={IconDefinitions.delete}
            className="text-danger"
            onClick={() => showDelete(props.dataItem)}
          />
        </td>
      )}
      </>
    ),
    [showDelete, canDeleteLocation]
  );
  
  const handleAddItem = async (item: any) => {
    await addItem(item);
    await userService.refreshUserInfo(); 
    gridRef.current?.refreshData(); 
  }

  return (
    <TitlelessCard bodyClassName="p-0">
      <ODataGrid
        ref={gridRef}
        getData={(oDataQuery) => {
          if(tenantAdmin || systemAdmin){
            const orgQuery  = !organizationFilterId ? [] : [`organizationId eq ${organizationFilterId}`]
            const queryFilter = createODataQueryFilter(oDataQuery, orgQuery);
            return getItems(`${queryFilter}&$expand=Organization`)
          }
          const locationFilter = [`(${userLocation?.map(id => `id eq ${id}`).join(' or ')})`]
          const orgQuery  = !organizationFilterId ? [] : [`organizationId eq ${organizationFilterId}`]
          const filterQuery = createODataQueryFilter(oDataQuery, [...locationFilter, ...orgQuery]);
          return getItems(filterQuery+`&$expand=Organization`)
        }}
        dataState={gridData}
        onRowClick={onClick}
        onRowMenuSelect={onSelect}
        sort={[{ field: "name", dir: "asc"}]}
        scrollable="scrollable"
        pageSize={10}
      >
        <ResponsiveGridColumn
          title="Name"
          field="name"
          width={"auto"}
          sortable={true}
        />
        <ResponsiveGridColumn
          title="Business Code"
          field="businessCode"
          sortable={true}
          width={"auto"}
          breakpoint={Breakpoints.md}
        />
        <ResponsiveGridColumn
          title="Organization"
          field="organization.name"
          sortable={true}
          width={"auto"}
          breakpoint={Breakpoints.lg}
        />
        {canDeleteLocation && (
          <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            sortable={false}
            filterable={false}
            headerCell={() => null}
            cell={commandsCell}
            width={50}
          />
        )}
        <MenuItem
          text="Open"
          data={{ action: "open" }}
          icon="hyperlink-open"
        />
        <MenuItem
          text="Open Tab"
          data={{ action: "opentab" }}
          icon="windows"
        />
        {canDeleteLocation && (
          <MenuItem
            text="Delete"
            data={{ action: "delete" }}
            icon={IconDefinitions.delete}
          />
        )}
      </ODataGrid>
      <AddLocationModal
        showState={showAddState}
        showAddState={showAddState}
        onSubmit={handleAddItem}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Delete ${deleteItemState.value?.name}`}
        message={`You are about to delete this location, it cannot be undone. Are you sure you want to delete ${deleteItemState.value.name}?`}
        confirmText="Delete"
        onConfirm={() => deleteItem(deleteItemState.value)}
      />
    </TitlelessCard>
  );
}

export default LocationsIndexPage;
